﻿/**
 * 风险-机遇量化评估表 · 扫码填写页（无需登录）
 * 通过 ?standalone=assessment-fill&t=TOKEN 打开，扫码或分享链接即可填写并同步至双模型矩阵
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';

const ASSESSMENTS_API = '/api/risk-opportunity-assessments';
const SCORE_IMPACT_LABELS = [
  { value: 5, short: '5分', desc: '灾难性/颠覆性' },
  { value: 4, short: '4分', desc: '重大/显著' },
  { value: 3, short: '3分', desc: '中等' },
  { value: 2, short: '2分', desc: '轻微' },
  { value: 1, short: '1分', desc: '可忽略' },
];
const SCORE_LIKELIHOOD_LABELS = [
  { value: 5, short: '5分', desc: '几乎确定' },
  { value: 4, short: '4分', desc: '很可能' },
  { value: 3, short: '3分', desc: '可能' },
  { value: 2, short: '2分', desc: '不太可能' },
  { value: 1, short: '1分', desc: '罕见' },
];
const SCORE_CONTROL_LABELS = [
  { value: 5, short: '5分', desc: '极难控制' },
  { value: 4, short: '4分', desc: '难以控制' },
  { value: 3, short: '3分', desc: '部分可控' },
  { value: 2, short: '2分', desc: '基本可控' },
  { value: 1, short: '1分', desc: '完全可控' },
];
const SCORE_CAPTURE_LABELS = [
  { value: 5, short: '5分', desc: '能力极弱' },
  { value: 4, short: '4分', desc: '能力较弱' },
  { value: 3, short: '3分', desc: '能力中等' },
  { value: 2, short: '2分', desc: '能力较强' },
  { value: 1, short: '1分', desc: '能力极强' },
];

function getBaseUrl() {
  if (typeof window === 'undefined') return '';
  return window.__BASE_URL__ || '';
}

export default function AssessmentFillStandalone() {
  const [token, setToken] = useState('');
  const [fillInfo, setFillInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingUnit, setLoadingUnit] = useState(false);
  const [error, setError] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [existingAssessments, setExistingAssessments] = useState([]);
  const [saving, setSaving] = useState(false);
  /** 用户选择的填写单位（先选单位再进时使用） */
  const [unitSelectId, setUnitSelectId] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedOrgName, setSelectedOrgName] = useState('');
  /** 部门折叠：集合内存放被折叠的部门名，点击部门头可切换 */
  const [collapsedDepts, setCollapsedDepts] = useState(() => new Set());
  const hasRestoredCollapsedRef = useRef(false);
  /** 致各模块一封信的填写人签名（canvas 导出 data URL），提交评估时会一并上传 */
  const [letterSignature, setLetterSignature] = useState('');
  const signatureCanvasRef = useRef(null);
  const signatureDrawingRef = useRef(false);
  const signatureLastRef = useRef({ x: 0, y: 0 });

  const t = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('t') || '';
  }, []);

  const s = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('s') || '';
  }, []);

  useEffect(() => {
    setToken(t);
  }, [t]);

  // 短链接：仅有 s= 时先解析出 token 再进填写流程
  const [shortResolved, setShortResolved] = useState(false);
  useEffect(() => {
    if (!s || t) {
      setShortResolved(true);
      return;
    }
    fetch(`${getBaseUrl()}${ASSESSMENTS_API}/fill-resolve?s=${encodeURIComponent(s)}`, { method: 'GET', headers: { Accept: 'application/json' } })
      .then((res) => res.json())
      .then((data) => {
        if (data.token) {
          setToken(data.token);
          const url = new URL(window.location.href);
          url.searchParams.delete('s');
          url.searchParams.set('t', data.token);
          window.history.replaceState({}, '', url.pathname + '?' + url.searchParams.toString());
        } else {
          setError(data.error || '链接无效或已过期');
          setLoading(false);
        }
      })
      .catch(() => {
        setError('网络错误，请稍后重试');
        setLoading(false);
      })
      .finally(() => setShortResolved(true));
  }, [s, t]);

  useEffect(() => {
    if (!shortResolved || !token) {
      if (!token && shortResolved) setError('缺少填写链接参数');
      if (!shortResolved) setLoading(true);
      else setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    fetch(`${getBaseUrl()}${ASSESSMENTS_API}/fill-info?t=${encodeURIComponent(token)}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.error) {
          setError(data.error);
          setFillInfo(null);
          return;
        }
        if (data.units?.length && !(data.items?.length)) {
          setFillInfo({ period: data.period, units: data.units });
          setExistingAssessments([]);
          setUnitSelectId(data.units[0]?.orgId || '');
        } else {
          setFillInfo({ orgName: data.orgName, period: data.period, items: data.items || [] });
          setExistingAssessments(data.existingAssessments || []);
          setSelectedOrgId(data.orgId || '');
          setSelectedOrgName(data.orgName || '');
        }
      })
      .catch(() => setError('网络错误，请稍后重试'))
      .finally(() => setLoading(false));
  }, [token, shortResolved]);

  /** 选择填写单位后进入评估表 */
  const handleEnterByUnit = useCallback(() => {
    if (!token || !unitSelectId) return;
    const unit = fillInfo?.units?.find((u) => u.orgId === unitSelectId);
    if (!unit) return;
    setLoadingUnit(true);
    fetch(`${getBaseUrl()}${ASSESSMENTS_API}/fill-info?t=${encodeURIComponent(token)}&orgId=${encodeURIComponent(unitSelectId)}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.error) {
          setError(data.error);
          return;
        }
        setFillInfo({ period: fillInfo.period, orgName: data.orgName || unit.orgName, items: data.items || [] });
        setExistingAssessments(data.existingAssessments || []);
        setSelectedOrgId(unitSelectId);
        setSelectedOrgName(data.orgName || unit.orgName || unitSelectId);
      })
      .catch(() => setError('网络错误，请稍后重试'))
      .finally(() => setLoadingUnit(false));
  }, [token, unitSelectId, fillInfo?.period, fillInfo?.units]);

  const byDept = useMemo(() => {
    if (!fillInfo?.items?.length) return [];
    const map = new Map();
    fillInfo.items.forEach((r) => {
      const dept = (r.riskManagementDept || r.responsibleDept || '').trim() || '未分配部门';
      if (!map.has(dept)) map.set(dept, { dept, risks: [], opportunities: [] });
      const g = map.get(dept);
      if ((r.type || 'risk') === 'opportunity') g.opportunities.push(r);
      else g.risks.push(r);
    });
    return Array.from(map.values());
  }, [fillInfo?.items]);

  const collapseStorageKey = useMemo(() => {
    if (!fillInfo?.period || !selectedOrgId) return null;
    return `assessment-fill-collapsed-${fillInfo.period}-${selectedOrgId}`;
  }, [fillInfo?.period, selectedOrgId]);

  useEffect(() => {
    if (!collapseStorageKey) return;
    hasRestoredCollapsedRef.current = false;
  }, [collapseStorageKey]);

  useEffect(() => {
    if (!collapseStorageKey || !byDept.length || hasRestoredCollapsedRef.current) return;
    hasRestoredCollapsedRef.current = true;
    try {
      const raw = localStorage.getItem(collapseStorageKey);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) setCollapsedDepts(new Set(arr));
      }
    } catch (_) {}
  }, [collapseStorageKey, byDept.length]);

  useEffect(() => {
    if (!collapseStorageKey) return;
    try {
      localStorage.setItem(collapseStorageKey, JSON.stringify([...collapsedDepts]));
    } catch (_) {}
  }, [collapseStorageKey, collapsedDepts]);

  /** 签名板：获取画布相对坐标（与 canvas 显示尺寸一致） */
  const getSignaturePoint = useCallback((e) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  }, []);

  const onSignatureStart = useCallback((e) => {
    e.preventDefault();
    const p = getSignaturePoint(e);
    if (p) {
      signatureDrawingRef.current = true;
      signatureLastRef.current = p;
      const ctx = signatureCanvasRef.current?.getContext('2d');
      if (ctx) {
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
      }
    }
  }, [getSignaturePoint]);

  const onSignatureMove = useCallback((e) => {
    e.preventDefault();
    if (!signatureDrawingRef.current) return;
    const p = getSignaturePoint(e);
    if (p) {
      const ctx = signatureCanvasRef.current?.getContext('2d');
      if (ctx) {
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
      }
      signatureLastRef.current = p;
    }
  }, [getSignaturePoint]);

  const onSignatureEnd = useCallback((e) => {
    e.preventDefault();
    if (signatureDrawingRef.current) {
      signatureDrawingRef.current = false;
      const canvas = signatureCanvasRef.current;
      if (canvas) try { setLetterSignature(canvas.toDataURL('image/png')); } catch (_) {}
    }
  }, []);

  const clearSignature = useCallback(() => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      setLetterSignature('');
    }
  }, []);

  /** 初始化签名板 canvas 尺寸与笔迹样式（必须在所有条件 return 之前声明，遵守 Hooks 规则） */
  useEffect(() => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width && rect.height) {
      canvas.width = rect.width;
      canvas.height = rect.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.strokeStyle = '#1e293b';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
      }
    }
  }, [fillInfo?.items?.length]);

  const handleOpen = (riskItem, existing) => {
    const notApplicable = existing?.notApplicable === true || existing?.level === 'na';
    setEditItem({
      riskListId: riskItem.id,
      itemName: riskItem.thirdLevelRisk || riskItem.thirdLevelRiskDesc || '—',
      itemDesc: riskItem.thirdLevelRiskDesc || '',
      itemType: (riskItem.type || 'risk') === 'opportunity' ? 'opportunity' : 'risk',
      module: riskItem.riskManagementDept || riskItem.responsibleDept || '',
      departments: [riskItem.riskManagementDept, riskItem.responsibleDept].filter(Boolean),
      notApplicable: notApplicable,
      scoreImpact: existing?.scoreImpact ?? null,
      scoreLikelihood: existing?.scoreLikelihood ?? null,
      scoreControl: existing?.scoreControl ?? null,
      scoreCapture: existing?.scoreCapture ?? null,
      id: existing?.id ?? null,
    });
  };

  const handleSave = async () => {
    if (!editItem || !token) return;
    const notApplicable = editItem.notApplicable === true;
    const payload = {
      token,
      riskListId: editItem.riskListId,
      itemName: editItem.itemName,
      itemType: editItem.itemType,
      module: editItem.module,
      departments: editItem.departments || [],
      notApplicable: notApplicable,
      scoreImpact: notApplicable ? null : (editItem.scoreImpact != null ? Number(editItem.scoreImpact) : null),
      scoreLikelihood: notApplicable ? null : (editItem.scoreLikelihood != null ? Number(editItem.scoreLikelihood) : null),
      scoreControl: notApplicable ? null : (editItem.scoreControl != null ? Number(editItem.scoreControl) : null),
      scoreCapture: notApplicable ? null : (editItem.scoreCapture != null ? Number(editItem.scoreCapture) : null),
    };
    if (editItem.id) payload.id = editItem.id;
    if (selectedOrgId) payload.orgId = selectedOrgId;
    if (selectedOrgName) payload.orgName = selectedOrgName;
    if (letterSignature) payload.letterSignature = letterSignature;
    setSaving(true);
    try {
      const res = await fetch(`${getBaseUrl()}${ASSESSMENTS_API}/fill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || '保存失败');
      setExistingAssessments((prev) => {
        const next = prev.filter((a) => a.id !== data.id && a.riskListId !== data.riskListId);
        next.push(data);
        return next;
      });
      setEditItem(null);
    } catch (err) {
      alert(err.message || '保存失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-slate-500 font-medium">加载中…</div>
      </div>
    );
  }

  if (error || !fillInfo) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">!</span>
          </div>
          <h2 className="text-lg font-semibold text-slate-800 mb-2">无法打开填写页</h2>
          <p className="text-slate-600 text-sm">{error || '链接无效或已过期'}</p>
          <p className="text-slate-500 text-xs mt-3">请向管理员重新获取扫码填写链接。</p>
        </div>
      </div>
    );
  }

  if (fillInfo.units?.length && !fillInfo.items?.length) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" style={{ minHeight: '100dvh' }}>
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-8 max-w-md w-full">
          <h2 className="text-lg font-semibold text-slate-800 mb-2">请选择填写单位</h2>
          <p className="text-slate-600 text-sm mb-4">评估周期：{fillInfo.period || '—'}</p>
          <label className="block text-sm font-medium text-slate-700 mb-2">填写单位</label>
          <select
            value={unitSelectId}
            onChange={(e) => setUnitSelectId(e.target.value)}
            className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm mb-4"
          >
            {fillInfo.units.map((u) => (
              <option key={u.orgId} value={u.orgId}>{u.orgName || u.orgId}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleEnterByUnit}
            disabled={loadingUnit}
            className="w-full py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium"
          >
            {loadingUnit ? '加载中…' : '进入评估表'}
          </button>
        </div>
      </div>
    );
  }

  const toggleDept = (dept) => {
    setCollapsedDepts((prev) => {
      const next = new Set(prev);
      if (next.has(dept)) next.delete(dept);
      else next.add(dept);
      return next;
    });
  };

  const expandAllDepts = () => setCollapsedDepts(new Set());
  const collapseAllDepts = () => setCollapsedDepts(new Set(byDept.map((g) => g.dept)));

  return (
    <div
      className="fixed inset-0 bg-slate-50 overflow-y-auto overflow-x-hidden overscroll-behavior-y-auto"
      style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}
    >
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 shadow-sm shrink-0">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <h1 className="text-lg font-semibold text-slate-900">风险-机遇量化评估表 · 扫码填写</h1>
          <p className="text-sm text-slate-500 mt-1">无需登录，填写后将同步至风险矩阵</p>
          <div className="flex flex-wrap gap-4 mt-3 text-sm">
            <span className="text-slate-600">填写单位：<strong className="text-slate-800">{fillInfo.orgName || '—'}</strong></span>
            <span className="text-slate-600">评估周期：<strong className="text-slate-800">{fillInfo.period || '—'}</strong></span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* 致各模块的一封信：填写前说明全面风险管理的重要性 */}
        <div className="rounded-xl border border-amber-200/80 shadow-sm p-5 mb-6 overflow-hidden" style={{ background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 50%, #fde68a 100%)' }}>
          <div className="flex items-start gap-4">
            {/* 圣小农卡通角色 */}
            <div className="flex-shrink-0 w-20 h-20 rounded-full bg-amber-100/90 border-2 border-amber-300/80 flex items-center justify-center overflow-hidden shadow-inner" title="圣小农">
              <img src="/圣小农.png" alt="圣小农" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-semibold text-slate-900 mb-3">致各模块的一封信</h2>
              <div className="text-sm text-slate-700 leading-relaxed space-y-2">
            <p>各业务模块负责人、同事：</p>
            <p>全面风险管理是公司稳健经营与战略落地的重要保障。通过系统识别、评估与应对风险与机遇，能够提前发现隐患、把握机会，支撑决策质量与目标达成。</p>
            <p>本周期「风险-机遇量化评估」旨在汇集各模块一线判断，形成集团统一视图。您的自评将直接用于双模型矩阵与后续管控，请结合本部门实际，客观、及时完成下列事项的评分。感谢您的参与与支持。</p>
            <p className="text-slate-600 pt-1">—— 风险管理与审计条线</p>
              </div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-amber-200/80">
            <p className="text-sm font-medium text-slate-700 mb-2">填写人签名</p>
            <div className="border border-slate-300 rounded-lg bg-slate-50 overflow-hidden">
              <canvas
                ref={signatureCanvasRef}
                className="w-full h-28 block touch-none"
                style={{ touchAction: 'none' }}
                onMouseDown={onSignatureStart}
                onMouseMove={onSignatureMove}
                onMouseUp={onSignatureEnd}
                onMouseLeave={onSignatureEnd}
                onTouchStart={onSignatureStart}
                onTouchMove={onSignatureMove}
                onTouchEnd={onSignatureEnd}
                onTouchCancel={onSignatureEnd}
              />
            </div>
            <div className="flex items-center gap-2 mt-2">
              <button
                type="button"
                onClick={clearSignature}
                className="text-xs px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-100"
              >
                清除重签
              </button>
              {letterSignature && <span className="text-xs text-slate-500">已签名</span>}
            </div>
          </div>
        </div>

        <p className="text-sm text-slate-600 mb-2">请对下列事项点击「自评」填写量化评分（1-5 分），提交后即可生效。</p>
        {byDept.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <button
              type="button"
              onClick={expandAllDepts}
              className="text-sm px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100"
            >
              一键展开
            </button>
            <button
              type="button"
              onClick={collapseAllDepts}
              className="text-sm px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100"
            >
              一键折叠
            </button>
            <span className="text-xs text-slate-500">展开/折叠状态已保存，下次打开将恢复</span>
          </div>
        )}

        {byDept.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500">当前暂无待评估事项</div>
        ) : (
          <div className="space-y-4 pb-6">
            {byDept.map((grp) => {
              const isCollapsed = collapsedDepts.has(grp.dept);
              return (
              <div key={grp.dept} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleDept(grp.dept)}
                  className="w-full px-4 py-2.5 bg-slate-100 border-b border-slate-200 font-medium text-slate-800 text-left flex items-center gap-2 hover:bg-slate-200 active:bg-slate-200"
                >
                  <span className={`inline-block transition-transform ${isCollapsed ? '' : 'rotate-90'}`}>▶</span>
                  {grp.dept} <span className="text-xs font-normal text-slate-500">（风险 {grp.risks.length} 条，机遇 {grp.opportunities.length} 条）</span>
                </button>
                {!isCollapsed && (
                <div className="divide-y divide-slate-100">
                  {grp.risks.map((r) => {
                    const existing = existingAssessments.find((a) => a.riskListId === r.id);
                    const name = r.thirdLevelRisk || r.thirdLevelRiskDesc || '—';
                    const desc = (r.thirdLevelRiskDesc || '').trim();
                    return (
                      <div key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                        <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-rose-100 text-rose-700 w-14 justify-center shrink-0">风险</span>
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-slate-900">{name}</span>
                          {desc && name !== desc && (
                            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{desc}</p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleOpen(r, existing)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 text-xs font-medium"
                        >
                          {existing ? '查看/编辑' : '自评'}
                        </button>
                        {existing && (
                          <span className="text-xs text-slate-500">
                            {existing.notApplicable === true || existing.level === 'na' ? '不适用' : `等级：${existing.level === 'high' ? '高' : existing.level === 'medium' ? '中' : existing.level === 'low' ? '低' : '—'}`}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {grp.opportunities.map((r) => {
                    const existing = existingAssessments.find((a) => a.riskListId === r.id);
                    const name = r.thirdLevelRisk || r.thirdLevelRiskDesc || '—';
                    const desc = (r.thirdLevelRiskDesc || '').trim();
                    return (
                      <div key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                        <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700 w-14 justify-center shrink-0">机遇</span>
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-slate-900">{name}</span>
                          {desc && name !== desc && (
                            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{desc}</p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleOpen(r, existing)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 text-xs font-medium"
                        >
                          {existing ? '查看/编辑' : '自评'}
                        </button>
                        {existing && (
                          <span className="text-xs text-slate-500">
                            {existing.notApplicable === true || existing.level === 'na' ? '不适用' : `等级：${existing.level === 'high' ? '高' : existing.level === 'medium' ? '中' : existing.level === 'low' ? '低' : '—'}`}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
                )}
              </div>
            );
            })}
            <div className="pt-6 pb-24 border-t border-slate-200 mt-6">
              <p className="text-sm text-slate-600 mb-3">完成自评后点击「提交」，已保存的 {existingAssessments.length} 条记录将确认写入自评记录表。</p>
              <button
                type="button"
                onClick={() => {
                  const n = existingAssessments.length;
                  if (n === 0) {
                    alert('您尚未保存任何自评，请先对事项点击「自评」并保存后再提交。');
                    return;
                  }
                  alert(`已提交！共 ${n} 条自评已保存至自评记录表。`);
                }}
                className="w-full py-3 rounded-xl bg-indigo-600 text-white font-medium hover:bg-indigo-700 text-base"
              >
                提交
              </button>
            </div>
          </div>
        )}
      </div>

      {editItem && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
          <div className="bg-white w-full max-h-[90vh] overflow-auto rounded-t-2xl sm:rounded-2xl shadow-xl max-w-lg">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">{editItem.itemName} · {editItem.itemType === 'opportunity' ? '机遇' : '风险'}</h3>
              <button type="button" onClick={() => setEditItem(null)} className="p-1 text-slate-500 hover:text-slate-800">×</button>
            </div>
            <div className="p-4 space-y-4">
              {editItem.itemDesc && (
                <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
                  <p className="text-xs font-medium text-slate-500 mb-0.5">{editItem.itemType === 'opportunity' ? '机遇说明' : '风险说明'}</p>
                  <p className="text-sm text-slate-700">{editItem.itemDesc}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">是否适用</label>
                <div className="flex gap-4">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="applicable"
                      checked={editItem.notApplicable !== true}
                      onChange={() => setEditItem((p) => ({ ...p, notApplicable: false }))}
                      className="text-indigo-600"
                    />
                    <span className="text-sm text-slate-700">适用</span>
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="applicable"
                      checked={editItem.notApplicable === true}
                      onChange={() => setEditItem((p) => ({ ...p, notApplicable: true }))}
                      className="text-indigo-600"
                    />
                    <span className="text-sm text-slate-700">不适用</span>
                  </label>
                </div>
                <p className="text-xs text-slate-500 mt-1">选择「不适用」则无需填写下方评分，直接保存即可。</p>
              </div>
              {editItem.notApplicable !== true && (
                <>
                  <p className="text-xs text-slate-500">第二部分评分将决定矩阵位置与等级，请按 1-5 分打分。</p>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">X 轴：影响/收益</label>
                <select
                  value={editItem.scoreImpact ?? ''}
                  onChange={(e) => setEditItem((p) => ({ ...p, scoreImpact: e.target.value ? Number(e.target.value) : null }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">请选择</option>
                  {SCORE_IMPACT_LABELS.map((o) => <option key={o.value} value={o.value}>{o.short} - {o.desc}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Y 轴：发生·实现可能性</label>
                <select
                  value={editItem.scoreLikelihood ?? ''}
                  onChange={(e) => setEditItem((p) => ({ ...p, scoreLikelihood: e.target.value ? Number(e.target.value) : null }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">请选择</option>
                  {SCORE_LIKELIHOOD_LABELS.map((o) => <option key={o.value} value={o.value}>{o.short} - {o.desc}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">风险：可控性 / 机遇：捕捉能力</label>
                <select
                  value={editItem.scoreControl ?? ''}
                  onChange={(e) => setEditItem((p) => ({ ...p, scoreControl: e.target.value ? Number(e.target.value) : null }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">请选择</option>
                  {SCORE_CONTROL_LABELS.map((o) => <option key={o.value} value={o.value}>{o.short}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">机遇：战略契合度（风险可不填）</label>
                <select
                  value={editItem.scoreCapture ?? ''}
                  onChange={(e) => setEditItem((p) => ({ ...p, scoreCapture: e.target.value ? Number(e.target.value) : null }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">请选择</option>
                  {SCORE_CAPTURE_LABELS.map((o) => <option key={o.value} value={o.value}>{o.short}</option>)}
                </select>
              </div>
                </>
              )}
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || (editItem.notApplicable !== true && (editItem.scoreImpact == null || editItem.scoreLikelihood == null))}
                  className="flex-1 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium"
                >
                  {saving ? '保存中…' : '保存'}
                </button>
                <button type="button" onClick={() => setEditItem(null)} className="px-4 py-2.5 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 text-sm">
                  取消
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
